import { Button, CircularProgress, Divider, Grid, Typography, Box, Paper, IconButton, Avatar, Stack, Chip, Link as MuiLink } from "@mui/material";
import { Close, Fingerprint, History, Link, Security, VerifiedUser } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import moment from "moment";

// Clients Details View Component
const ClientsDetail = ({ handleViewModalClose, clientId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();

  const { isLoading, data, isFetching, error } = useQuery(["client_detail", clientId], () => {
    return axiosPrivate.get(`/clients/${clientId}`);
  }, { refetchOnWindowFocus: false });

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error?.response?.data?.error?.message || error?.message || "Network Error!", {
        variant: "error",
        autoHideDuration: 2000
      });
    }
  }, [enqueueSnackbar, error]);

  const client = data?.data?.data;

  return (
    <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '560px' },
          borderRadius: { xs: '24px 24px 0 0', sm: 6 },
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '95vh'
        }}
      >
        <IconButton
          onClick={handleViewModalClose}
          sx={{ position: 'absolute', right: 16, top: 16, color: 'text.disabled' }}
        >
          <Close />
        </IconButton>

        <Box sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={client?.logo_url}
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.lighter',
                boxShadow: '0 8px 25px rgba(255,170,0,0.2)',
                mb: 2,
                borderRadius: 3
              }}
            >
              <VerifiedUser sx={{ fontSize: 40, color: 'primary.main' }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
              {client?.name}
            </Typography>
            <Chip
              label={client?.client_type}
              size="small"
              sx={{ mt: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: '10px', bgcolor: 'rgba(0,0,0,0.05)' }}
            />
          </Box>

          {isLoading || isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={2.5} sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 1 }}>
              <DetailSection title="Core Information">
                <DetailItem icon={<Fingerprint fontSize="small" />} label="Client Identifier" value={client?.id} />
                <DetailItem icon={<History fontSize="small" />} label="Registered On" value={moment(client?.created_at).format("LLLL")} />
              </DetailSection>

              <DetailSection title="Security & Access">
                <DetailItem icon={<Security fontSize="small" />} label="Authorized Scopes" value={client?.scopes} colored />
                <DetailItem
                  icon={<Link fontSize="small" />}
                  label="Redirect URIs"
                  value={client?.redirect_uris?.join("\n") || "None"}
                  multiline
                />
              </DetailSection>

              {(client?.terms_of_service_url || client?.privacy_policy_url) && (
                <DetailSection title="Legal Documents">
                  {client?.privacy_policy_url && (
                    <DetailItem
                      icon={<Link fontSize="small" />}
                      label="Privacy Policy URL"
                      value={
                        <MuiLink href={client.privacy_policy_url} target="_blank" rel="noopener noreferrer">
                          {client.privacy_policy_url}
                        </MuiLink>
                      }
                      multiline
                    />
                  )}
                  {client?.terms_of_service_url && (
                    <DetailItem
                      icon={<Link fontSize="small" />}
                      label="Terms of Service URL"
                      value={
                        <MuiLink href={client.terms_of_service_url} target="_blank" rel="noopener noreferrer">
                          {client.terms_of_service_url}
                        </MuiLink>
                      }
                      multiline
                    />
                  )}
                </DetailSection>
              )}

              {client?.post_logout_redirect_uris?.length > 0 && (
                <DetailSection title="Logout Configurations">
                  <DetailItem
                    icon={<Link fontSize="small" />}
                    label="Post-Logout URIs"
                    value={client?.post_logout_redirect_uris?.join("\n")}
                    multiline
                  />
                  {client?.backchannel_logout_uri && (
                    <DetailItem icon={<Link fontSize="small" />} label="Backchannel URI" value={client?.backchannel_logout_uri} />
                  )}
                </DetailSection>
              )}
            </Stack>
          )}

          <Box sx={{ mt: 4 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{ borderRadius: 3, py: 1.5, fontWeight: 700, textTransform: 'none', bgcolor: 'secondary.main' }}
              onClick={handleViewModalClose}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

function DetailSection({ title, children }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', mb: 1, display: 'block', letterSpacing: '1px' }}>
        {title}
      </Typography>
      <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 4 }}>
        <Stack spacing={2}>{children}</Stack>
      </Paper>
    </Box>
  );
}

function DetailItem({ icon, label, value, colored, multiline }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <Box sx={{ color: 'primary.main', mt: 0.5 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>{label}</Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: colored ? 'primary.main' : 'secondary.main',
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
            wordBreak: 'break-all'
          }}
        >
          {value || 'N/A'}
        </Typography>
      </Box>
    </Box>
  );
}

export default ClientsDetail;
